
import { forwardRef, useCallback, useContext } from 'react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import clsx from 'clsx'

import { type CalendarDay, type DateAnswers, type DateAnswersMap, type DayCount } from '@coorum-research/shared-common/types/calendar.type'
import { formatDate } from '@coorum-research/shared-common/utils/date.util'
import { isNextMonthVisible, isPrevMonthVisible } from '@coorum-research/shared-common/utils/calendar.util'

import { LeftArrowIcon } from './icons/LeftArrowIcon'
import { RightArrowIcon } from './icons/RightArrowIcon'
import { LoadingIcon } from './icons/LoadingIcon'
import { ThemeContext } from './ThemeContext'


const HEAD_LABELS = ['日', '月', '火', '水', '木', '金', '土'] as const

type CellProps = {
  day: CalendarDay
  isFocusable: boolean
  mainColor: string
};

/**
 * カレンダーの Grid Cell
 */
const Cell = forwardRef<HTMLDivElement, CellProps>(({ day, isFocusable, mainColor, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={clsx(
        { 'cursor-pointer': isFocusable },
        'flex size-9 items-center justify-center rounded-full text-[15.65px]/[20.86px] font-bold [&[data-state=open]]:brightness-90'
      )}
      style={{ backgroundColor: isFocusable ? mainColor : '#fff' }}
      {...props}
    >
      {day}
    </div>
  )
})

type CellWithMenuProps = {
  day: CalendarDay
  answers: DateAnswers
  onDayClick: (day: CalendarDay) => void
  onAnswerSelect: (answerId: string) => void
  mainColor: string
};

/**
 * DropdownMenu を含めたカレンダーの Grid Cell
 * @param param0 
 * @returns 
 */
function CellWithMenu({ day, answers, onDayClick, onAnswerSelect, mainColor }: CellWithMenuProps) {
  const handleOpenChange = useCallback((openState: boolean) => {
    if (openState) {
      onDayClick(day)
    }
  }, [day, onDayClick])

  const handleSelect = useCallback((id: string) => {
    setTimeout(() => {
      onAnswerSelect(id)
    })
  }, [onAnswerSelect])

  return (
    <DropdownMenu.Root
      onOpenChange={handleOpenChange}
    >
      <DropdownMenu.Trigger asChild>
        <Cell
          day={day}
          isFocusable
          mainColor={mainColor}
        />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="z-[2147483647] w-[170px] rounded-[6px] border border-[rgba(27,28,27,0.12)] bg-white p-2 shadow-[0_1px_6px_2px_rgba(0,0,0,0.06),0_2px_3px_0_rgba(0,0,0,0.12)]" sideOffset={5}
        >
          {answers.length > 0 ? answers.map(answer => (
            <DropdownMenu.Item 
              key={answer.id}
              className="cursor-pointer truncate p-2 text-sm"
              onSelect={() => handleSelect(answer.id)}
            >
              {answer.surveyName}
            </DropdownMenu.Item>
          )) : <div className="flex animate-spin justify-center"><LoadingIcon width={32} height={32} fill={mainColor} /></div>}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}

type CalendarProps = {
  year: number
  month: number
  dayCounts: DayCount[]
  dateAnswersMap: DateAnswersMap
  oldestAnswerDate: string | null | undefined
  onBack: () => void
  onForward: () => void
  onDayClick: (day: CalendarDay) => void
  onAnswerSelect: (answerId: string) => void
};

/**
 * マイページのカレンダー
 * @param param0 
 * @returns 
 */
export function Calendar({ year, month, dayCounts, dateAnswersMap, oldestAnswerDate, onBack, onForward, onDayClick, onAnswerSelect }: CalendarProps) {
  const { mainColor } = useContext(ThemeContext)
  const prevMonthVisible = typeof oldestAnswerDate === 'string' ? isPrevMonthVisible(year, month, new Date(oldestAnswerDate)) : false
  const nextMonthVisible = isNextMonthVisible(year, month, new Date())

  return (
    <>
      <div className='mx-auto grid w-[226px] grid-cols-[36px_auto_36px]'>
        <button className={clsx('justify-self-start p-1', { 'invisible': !prevMonthVisible })} onClick={onBack}><LeftArrowIcon /></button>
        <div className='flex items-center gap-2 justify-self-center'>
          <p className='text-sm'>{year}</p>
          <p className='font-bold'>{month}月</p>
        </div>
        <button className={clsx('justify-self-end p-1', { 'invisible': !nextMonthVisible })} onClick={onForward}><RightArrowIcon /></button>
      </div>
      <div className='mx-auto mt-2.5 grid w-[285px] grid-cols-7 gap-1'>
        {HEAD_LABELS.map(y => <div key={y} className='flex h-[20px] w-9 items-center justify-center text-xs font-bold text-opacity-70'>{y}</div>)}
        {dayCounts.map((dayCount, idx) => {
          if (dayCount.count > 0) {
            const date = formatDate(new Date(year, month - 1, +dayCount.day), 'yyyy-MM-dd')
            const answers = dateAnswersMap[date] ?? []
            return (
              <CellWithMenu
                key={idx}
                day={dayCount.day}
                answers={answers}
                onDayClick={onDayClick}
                onAnswerSelect={onAnswerSelect}
                mainColor={mainColor}
              />
            )
          }
          return (
            <Cell
              key={idx}
              day={dayCount.day}
              isFocusable={false}
              mainColor={mainColor}
            />
          )
        })}
      </div>
    </>
  )
}
